import { useEffect, useState } from "react";
import { Loader2, BadgeCheck, MapPin, BookOpen, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function ClaimInstitute() {
  const { refresh } = useAuth();
  const [claimable, setClaimable] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api.get("/institutes/claimable")
      .then((r) => setClaimable(r.data))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const claim = async (id) => {
    try { await api.post(`/institutes/${id}/claim`); toast.success("Claim submitted — pending admin approval"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to claim"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight">Claim your institute</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Your courses may already be on Medlane — added by our team to get you started. Claim your institute below to
        manage its courses and applicants. A Medlane admin confirms each claim.
      </p>
      <div className="mt-4">
        <Button variant="outline" size="sm" data-testid="claim-refresh-btn" onClick={() => refresh()}>
          <RefreshCw className="mr-2 h-4 w-4" /> I've been approved — refresh
        </Button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {claimable.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground sm:col-span-2">
              No unclaimed institutes available right now. Please ask the Medlane admin to add your institute, then refresh.
            </div>
          )}
          {claimable.map((i) => (
            <div key={i.id} data-testid={`claimable-${i.id}`} className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <div className="h-28 overflow-hidden"><img src={i.cover_url} alt="" className="h-full w-full object-cover" /></div>
              <div className="p-5">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-heading text-lg font-bold">{i.name}</h3>
                  {i.verified && <BadgeCheck className="h-5 w-5 shrink-0 text-primary" />}
                </div>
                <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {i.city}, {i.state}</p>
                <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-primary"><BookOpen className="h-4 w-4" /> {i.course_count} course{i.course_count !== 1 ? "s" : ""}</p>
                <div className="mt-4">
                  {i.claim_status === "pending" ? <Badge variant="secondary" data-testid={`claim-pending-${i.id}`}>Claim pending approval</Badge>
                    : i.claim_status === "rejected" ? <Badge variant="destructive">Claim rejected</Badge>
                    : <Button size="sm" className="w-full" data-testid={`claim-btn-${i.id}`} onClick={() => claim(i.id)}>Claim this institute</Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
